// eslint-disable-next-line no-restricted-imports
import { Tooltip, TooltipProps, makeStyles, useTheme } from '@material-ui/core';
import { BackstageTheme } from '@mainstage/theme';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

export enum Severity {
  Default,
  Info,
  Warning,
  Error,
}

const useStyles = makeStyles(() => ({
  tooltip: ({
    textColor,
    backgroundColor,
  }: {
    textColor?: string;
    backgroundColor?: string;
  }) => ({
    ...(textColor ? { color: textColor } : {}),
    ...(backgroundColor ? { backgroundColor } : {}),
  }),
}));

export interface WbTooltipProps extends Omit<TooltipProps, 'open'> {
  open?: boolean;
  setOpen?: Dispatch<SetStateAction<boolean>>;
  severity?: Severity;
}

export const WbTooltip = (props: WbTooltipProps) => {
  const {
    title,
    children,
    open: openProp,
    setOpen: setOpenProp,
    severity,
    ...rest
  } = props;

  const theme = useTheme() as BackstageTheme;

  let textColor: string | undefined;
  let backgroundColor: string | undefined;

  switch (severity) {
    case Severity.Info:
      textColor = theme.palette.info.contrastText;
      backgroundColor = theme.palette.info.light;
      break;
    case Severity.Warning:
      textColor = theme.palette.warning.contrastText;
      backgroundColor = theme.palette.warning.light;
      break;
    case Severity.Error:
      textColor = theme.palette.banner.text;
      backgroundColor = theme.palette.banner.error;
      break;
    default:
      break;
  }

  const classes = useStyles({ textColor, backgroundColor });
  const tooltipClasses =
    textColor && backgroundColor ? { tooltip: classes.tooltip } : undefined;

  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = openProp !== undefined && setOpenProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = isControlled ? setOpenProp : setInternalOpen;

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handler = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        handleClose();
      }
    },
    [open, handleClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [handler]);

  return (
    <Tooltip
      classes={tooltipClasses}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      title={title}
      {...rest}
    >
      {children}
    </Tooltip>
  );
};
